// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Group, Loader, Paper, Text, Textarea, Title } from '@mantine/core';
import type { JSX } from 'react';
import sectionClasses from './dashboard-section.module.css';

interface TreatmentPlanSectionProps {
  description: string | undefined;
  loading: boolean;
}

/**
 * Renders the patient's active care-plan summary as a read-only paragraph.
 * The mockup uses a Textarea-style block; we keep that affordance so the
 * read-only text wraps into a familiar bordered panel.
 */
export function TreatmentPlanSection({ description, loading }: TreatmentPlanSectionProps): JSX.Element {
  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (!description) {
    body = (
      <Text c="dimmed" py="md">
        You do not have a treatment prescribed.
      </Text>
    );
  } else {
    body = (
      <Textarea
        value={description}
        readOnly
        autosize
        minRows={3}
        maxRows={12}
        styles={{ input: { backgroundColor: 'var(--mantine-color-white)' } }}
      />
    );
  }
  return (
    <Paper shadow="sm" radius="md" p="lg" className={sectionClasses.decorated}>
      <Title order={3} mb="md">
        Treatment Plan
      </Title>
      {body}
    </Paper>
  );
}
